import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { loadKliTemplate, makeKli, contactShadow } from './kli.js';

/*
 * Every Kli on a page is drawn by ONE WebGL renderer on a fixed, full-window canvas.
 * Each [data-kli="name"] element is a "window": its own scene and camera, drawn with a
 * scissor rect over the element's box. One context, any number of creatures.
 *
 * cast[name] = { shells?, bubble?, frame: [centerX, centerY, width, height], dress(kli) -> outfit }
 * `frame` is the world box the camera must fit; `outfit` is { name: accessory }.
 */

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const VIEW_DIR = new THREE.Vector3(0, 0.16, 1).normalize();

export async function startKliStage({ canvas, cast, nav = document.querySelector('.nav') }) {
  const small = innerWidth < 700;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.toneMapping = THREE.NeutralToneMapping;   // keeps the brand blue true
  renderer.setClearColor(0x000000, 0);
  renderer.autoClear = false;
  const environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture;

  await loadKliTemplate();

  const views = [...document.querySelectorAll('[data-kli]')].map((el, i) => {
    const c = cast[el.dataset.kli];
    const scene = new THREE.Scene();
    scene.environment = environment;
    const key = new THREE.DirectionalLight(0xfff4e8, 2.0);
    key.position.set(-3, 4, 4.5);
    scene.add(key, new THREE.HemisphereLight(0xffffff, 0xE0F3FA, 0.75));   // ground bounce: --k-100

    const kli = makeKli({ shells: Math.round((c.shells ?? 30) * (small ? 0.7 : 1)), bubble: !!c.bubble, phase: i * 1.3 });
    scene.add(kli.root, contactShadow());
    const outfit = c.dress ? c.dress(kli) : {};
    Object.values(outfit).forEach((a) => kli.add(a));

    el.addEventListener('pointerdown', () => kli.poke());
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); kli.poke(); }
    });
    el.closest('[data-hop]')?.addEventListener('pointerenter', () => kli.jump());   // hovering a card makes its Kli hop

    return { name: el.dataset.kli, el, scene, key, kli, outfit, frame: c.frame, seen: false, size: '',
      camera: new THREE.PerspectiveCamera(24, 1, 0.1, 60), lookOverride: null };
  });

  const pointer = { x: innerWidth / 2, y: innerHeight / 3 };
  addEventListener('pointermove', (e) => { pointer.x = e.clientX; pointer.y = e.clientY; });

  function fitCamera(v, w, h) {
    const [cx, cy, fw, fh] = v.frame;
    const cam = v.camera;
    cam.aspect = w / h;
    const t = Math.tan(THREE.MathUtils.degToRad(cam.fov / 2));
    const dist = Math.max(fh / 2 / t, fw / 2 / (t * cam.aspect)) + 1.0;
    const look = new THREE.Vector3(cx, cy, 0);
    cam.position.copy(look).addScaledVector(VIEW_DIR, dist);
    cam.lookAt(look);
    cam.updateProjectionMatrix();
  }

  const timer = new THREE.Timer();
  timer.connect?.(document);
  renderer.setAnimationLoop((now) => {
    timer.update(now);
    const dt = Math.min(timer.getDelta(), 0.05);
    const t = timer.getElapsed();
    const W = innerWidth, H = innerHeight;
    const pr = renderer.getPixelRatio();
    if (canvas.width !== Math.floor(W * pr) || canvas.height !== Math.floor(H * pr)) renderer.setSize(W, H, false);
    const topEdge = nav ? nav.getBoundingClientRect().bottom : 0;   // never draw under the sticky nav

    renderer.setScissorTest(false);
    renderer.clear();
    renderer.setScissorTest(true);

    for (const v of views) {
      const r = v.el.getBoundingClientRect();
      if (r.bottom < topEdge || r.top > H || r.right < 0 || r.left > W || r.width < 2 || r.height < 2) continue;
      if (getComputedStyle(v.el).visibility === 'hidden') continue;
      if (!v.seen) { v.seen = true; setTimeout(() => v.kli.jump(), 250); }   // says hi when it scrolls into view

      const size = `${r.width}x${r.height}`;
      if (v.size !== size) { fitCamera(v, r.width, r.height); v.size = size; }

      if (v.lookOverride) v.kli.look.copy(v.lookOverride);
      else {
        const cx = r.left + r.width / 2, cy = r.top + r.height * 0.45;
        v.kli.look.set(
          THREE.MathUtils.clamp((pointer.x - cx) / (W / 2), -1, 1),
          THREE.MathUtils.clamp((pointer.y - cy) / (H / 2), -1, 1));
      }
      v.kli.update(dt, t, v.camera, v.key);

      const clipTop = Math.max(r.top, topEdge);
      renderer.setViewport(r.left, H - r.bottom, r.width, r.height);
      renderer.setScissor(r.left, H - r.bottom, r.width, Math.max(0, r.bottom - clipTop));
      renderer.render(v.scene, v.camera);
    }
  });

  document.body.classList.add('klis-ready');
  return { views, find: (name) => views.find((v) => v.name === name), reducedMotion };
}
